import React from "react";

/**
 * TruckProgressBar Component
 * Displays an interactive timeline progress bar showing trip stops/milestones
 * Designed for live tracking with animated truck icon and stop indicators
 *
 * @param {number} totalStops - Total number of stops in the trip (default: 8)
 * @param {number} currentStop - Current stop position, 1-indexed (default: 3)
 * @param {boolean} showLabels - Show start/current/end labels (default: true)
 * @param {number} height - Height of progress bar in pixels (default: 8)
 * @param {string} completedColor - Color for completed segments (default: #3B82F6 blue)
 * @param {string} activeColor - Color for current/active stop (default: #3B82F6)
 * @param {string} inactiveColor - Color for inactive/future segments (default: #E5E7EB light gray)
 */
export default function TruckProgressBar({
  totalStops = 8,
  currentStop = 3,
  showLabels = true,
  height = 3,
  completedColor = "#3B82F6",
  activeColor = "#3B82F6",
  inactiveColor = "#E5E7EB",
}) {
  return (
    <div className="truck-progress-wrapper">
      {/* Main timeline progress bar */}
      <div className="truck-progress-timeline">
        {/* Start circle - always active blue */}
        <div
          className="progress-stop progress-stop-start"
          style={{
            width: height + 4,
            height: height + 4,
            borderRadius: "50%",
            backgroundColor: completedColor,
            flexShrink: 0,
            boxShadow: `0 0 0 3px rgba(59, 130, 246, 0.1)`,
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
        />

        {/* All stops and connectors */}
        {Array.from({ length: Math.max(0, totalStops - 1) }).map((_, index) => {
          const stopNumber = index + 1;
          const isCompleted = stopNumber < currentStop;
          const isCurrentStop = stopNumber === currentStop;

          return (
            <React.Fragment key={`stop-${index}`}>
              {/* Connecting line */}
              <div
                className="progress-line"
                style={{
                  flex: 1,
                  height,
                  backgroundColor: isCompleted ? completedColor : inactiveColor,
                  borderRadius: "2px",
                  transition: "background-color 0.3s ease",
                }}
              />

              {/* Stop circle OR truck icon at current stop */}
              {isCurrentStop ? (
                // Truck at current stop
                <div
                  className="truck-icon-container"
                  style={{
                    width: height + 10,
                    height: height + 10,
                    borderRadius: "3px",
                    backgroundColor: activeColor,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: height > 6 ? "12px" : "10px",
                    fontWeight: "bold",
                    boxShadow: `0 2px 8px rgba(59, 130, 246, 0.3), 0 0 0 4px rgba(59, 130, 246, 0.15)`,
                    cursor: "pointer",
                    animation: "truckSlide 0.8s ease-out",
                  }}
                >
                  🚚
                </div>
              ) : (
                // Regular stop circle
                <div
                  className={`progress-stop ${isCompleted ? "completed" : ""}`}
                  style={{
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    backgroundColor: isCompleted
                      ? completedColor
                      : inactiveColor,
                    flexShrink: 0,
                    boxShadow: isCompleted
                      ? "inset 0 0 0 1px rgba(255, 255, 255, 0.3)"
                      : "none",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <style>{`
        .truck-progress-wrapper {
          width: 100%;
          padding: 8px 0;
        }

        .truck-progress-timeline {
          display: flex;
          align-items: center;
          gap: 3px;
          width: 100%;
          animation: slideIn 0.6s ease-out;
        }

        .progress-stop {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .progress-stop:hover {
          transform: scale(1.2);
        }

        .progress-stop.active {
          animation: pulse 2s infinite;
        }

        .progress-stop.completed {
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3) !important;
        }

        .progress-line {
          flex-shrink: 1;
          transition: background-color 0.3s ease;
        }

        .truck-icon-container {
          transition: all 0.3s ease;
        }

        .truck-icon-container:hover {
          transform: scale(1.1);
          box-shadow: 0 3px 12px rgba(59, 130, 246, 0.4) !important;
        }

        .progress-labels {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 6px;
          padding: 0 2px;
          font-size: 11px;
        }

        .label-text {
          font-size: 11px;
          font-weight: 500;
          color: #9CA3AF;
        }

        .label-current {
          color: #3B82F6;
          font-weight: 600;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.05);
          }
        }

        @keyframes truckSlide {
          from {
            opacity: 0;
            transform: scale(0.5) translateX(-15px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
